import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { TIME_SLOTS } from "./calendar.helpers";

interface CalendarDateTimeDialogProps {
  open: boolean;
  title: string;
  daysMatrix: {
    matrix: (number | null)[][];
    year: number;
    month: number;
  };
  selectedDateLabel: string;
  selectedTime: string;
  onMonthChange: (delta: -1 | 1) => void;
  onDayClick: (day: number) => void;
  onTimeClick: (time: string) => void;
  onClear: () => void;
  onClose: () => void;
  onConfirm: () => void;
  minDate?: Date | null;
}

const CalendarDateTimeDialog: React.FC<CalendarDateTimeDialogProps> = ({
  open,
  title,
  daysMatrix,
  selectedDateLabel,
  selectedTime,
  onMonthChange,
  onDayClick,
  onTimeClick,
  onClear,
  onClose,
  onConfirm,
  minDate,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 2,
          pb: 1,
        }}
      >
        <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{title}</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2 }}>
        <Grid container spacing={3}>
          {/* Calendário */}
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <IconButton onClick={() => onMonthChange(-1)}>{"<"}</IconButton>
              <Typography sx={{ fontWeight: 600 }}>
                {new Date(
                  daysMatrix.year,
                  daysMatrix.month,
                  1
                ).toLocaleDateString("pt-BR", {
                  month: "long",
                  year: "numeric",
                })}
              </Typography>
              <IconButton onClick={() => onMonthChange(1)}>{">"}</IconButton>
            </Box>

            {/* Cabeçalho semana */}
            <Grid container spacing={0.5} sx={{ mb: 1 }}>
              {["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"].map((d) => (
                <Grid item xs={12 / 7} key={d}>
                  <Typography
                    align="center"
                    sx={{ fontSize: 12, fontWeight: 600, color: "#777" }}
                  >
                    {d}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            {/* Dias */}
            <Grid container spacing={0.5}>
              {daysMatrix.matrix.map((week, wi) =>
                week.map((day, di) => {
                  if (!day) {
                    return (
                      <Grid item xs={12 / 7} key={`${wi}-${di}`}>
                        <Box sx={{ height: 32 }} />
                      </Grid>
                    );
                  }

                  const dayDate = new Date(
                    daysMatrix.year,
                    daysMatrix.month,
                    day
                  );

                  // normaliza para comparar apenas a parte de data (sem hora)
                  const isBeforeMin =
                    minDate != null &&
                    new Date(
                      dayDate.getFullYear(),
                      dayDate.getMonth(),
                      dayDate.getDate()
                    ).getTime() <
                      new Date(
                        minDate.getFullYear(),
                        minDate.getMonth(),
                        minDate.getDate()
                      ).getTime();

                  const isSelectedLabel =
                    selectedDateLabel &&
                    selectedDateLabel.startsWith(String(day).padStart(2, "0"));

                  const isDisabled = isBeforeMin;

                  return (
                    <Grid item xs={12 / 7} key={`${wi}-${di}`}>
                      <Button
                        fullWidth
                        onClick={() => !isDisabled && onDayClick(day)}
                        disabled={isDisabled}
                        sx={{
                          minWidth: 0,
                          height: 32,
                          borderRadius: 1,
                          fontSize: 13,
                          ...(isSelectedLabel && !isDisabled
                            ? {
                                bgcolor: "#D91F05",
                                color: "#FFFFFF",
                                "&:hover": { bgcolor: "#bf1a04" },
                              }
                            : isDisabled
                            ? {
                                bgcolor: "#F5F5F5",
                                color: "#BDBDBD",
                                border: "1px solid #E0E0E0",
                                "&:hover": { bgcolor: "#F5F5F5" },
                              }
                            : {
                                bgcolor: "#FFFFFF",
                                border: "1px solid #E0E0E0",
                                color: "#333333",
                                "&:hover": { bgcolor: "#F5F5F5" },
                              }),
                        }}
                      >
                        {day}
                      </Button>
                    </Grid>
                  );
                })
              )}
            </Grid>

            {/* Dia e hora selecionados */}
            <Box sx={{ mt: 3 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
                Dia e hora selecionados
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box
                  sx={{
                    flex: 1,
                    border: "1px solid #E0E0E0",
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                    fontSize: 14,
                  }}
                >
                  {selectedDateLabel || "--/--/----"}
                </Box>
                <Box
                  sx={{
                    width: 100,
                    border: "1px solid #E0E0E0",
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                    fontSize: 14,
                  }}
                >
                  {selectedTime || "--:--"}
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Horários */}
          <Grid item xs={12} md={5}>
            <Grid container spacing={1}>
              {TIME_SLOTS.map((time) => {
                const isSelected = time === selectedTime;
                return (
                  <Grid item xs={4} key={time}>
                    <Button
                      fullWidth
                      onClick={() => onTimeClick(time)}
                      sx={{
                        borderRadius: 1,
                        height: 40,
                        fontSize: 14,
                        ...(isSelected
                          ? {
                              bgcolor: "#D91F05",
                              color: "#FFFFFF",
                              "&:hover": { bgcolor: "#bf1a04" },
                            }
                          : {
                              bgcolor: "#FFFFFF",
                              border: "1px solid #E0E0E0",
                              color: "#333333",
                              "&:hover": { bgcolor: "#F5F5F5" },
                            }),
                      }}
                    >
                      {time}
                    </Button>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={onClear}
          sx={{ textTransform: "none", color: "#777777" }}
        >
          Limpar
        </Button>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            onClick={onClose}
            sx={{ textTransform: "none", color: "#D91F05" }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            sx={{
              textTransform: "none",
              bgcolor: "#D91F05",
              "&:hover": { bgcolor: "#bf1a04" },
            }}
            disabled={!selectedDateLabel || !selectedTime}
          >
            Confirmar
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CalendarDateTimeDialog;
